import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import { SUBSCRIPTION_REQUIRED } from './flags';
import PageSkeleton from '../components/ui/PageSkeleton';

/**
 * Gate for subscription-required routes. It does NOT render the paywall itself
 * (that's SubscribePage) and it does NOT make supervisor-vs-learner landing
 * decisions:
 *
 *   - subscribed    -> render the gated page
 *   - unsubscribed  -> redirect to /subscribe (the full-screen paywall)
 *
 * Crucially, an unsubscribed supervisor is sent to /subscribe here too, not to
 * /family. Routing a supervisor to /family belongs to the DEFAULT LANDING only
 * (see HomeRedirect); doing it in this per-route gate trapped supervisors —
 * every gated page they opened (e.g. Settings -> Subscribe) bounced them
 * straight back to the parent portal. /subscribe itself is not gated and offers
 * a free "continue to the parent portal" path for supervisors who land here.
 */
export default function RequireSubscription({ children }) {
    const { isSubscribed, subscriptionLoading, ready } = useAuth();

    if (!SUBSCRIPTION_REQUIRED) return <>{children}</>;

    // Hold the frame with a skeleton rather than a full-bleed "Loading…" — this
    // gate fires before the destination page's own loader.
    if (!ready || subscriptionLoading) {
        return <PageSkeleton label="Checking your subscription" />;
    }

    if (isSubscribed) return <>{children}</>;

    return <Navigate to="/subscribe" replace />;
}
